/**
 * Investor deck for LMcad. Slide-by-slide, full-viewport, scroll-snap
 * navigation. Keyboard arrows / spacebar / page-up-down all work, plus
 * click chevrons in the corner. Each slide also page-breaks cleanly so
 * "File > Print > Save as PDF" produces a real deck PDF.
 *
 * Numbers in this deck are sourced from research dumps (B2B from
 * Future Market Report, ACMA, Zinnov-NASSCOM, Berylls; B2C from Wohlers,
 * Grand View, IMARC India; Education from ABET and AICTE 2025-26).
 * If you change a stat, update the citation footer on that slide.
 *
 * Pitched with India in the lead · most readers of this deck will be
 * Indian VCs and Indian engineering buyers. Global numbers come second.
 */
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { Brand } from '../components/atelier';
import { CALENDLY_URL, CONTACT_EMAIL } from '../lib/links';

const TOTAL_SLIDES = 17;

export default function Deck() {
  const [slide, setSlide] = useState(0);
  const [navVisible, setNavVisible] = useState(false);
  const navTimerRef = useRef<number | null>(null);

  // Auto-hide nav: any mouse motion (or scroll) wakes the buttons,
  // then a 2.2s idle timer fades them back out. Hovering the group
  // pauses the timer so it stays visible while the user aims.
  const wakeNav = useCallback(() => {
    setNavVisible(true);
    if (navTimerRef.current !== null) {
      window.clearTimeout(navTimerRef.current);
    }
    navTimerRef.current = window.setTimeout(() => {
      setNavVisible(false);
    }, 2200);
  }, []);

  useEffect(() => {
    const onActivity = () => wakeNav();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('scroll', onActivity, true);
    window.addEventListener('touchstart', onActivity);
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('scroll', onActivity, true);
      window.removeEventListener('touchstart', onActivity);
      if (navTimerRef.current !== null) {
        window.clearTimeout(navTimerRef.current);
      }
    };
  }, [wakeNav]);

  const scrollToSlide = useCallback((idx: number) => {
    const el = document.querySelector<HTMLElement>(`.slide[data-slide="${idx}"]`);
    const container = document.querySelector<HTMLElement>('.deck');
    if (el && container) {
      container.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
    }
  }, []);

  const next = useCallback(() => {
    setSlide((s) => {
      const n = Math.min(s + 1, TOTAL_SLIDES - 1);
      scrollToSlide(n);
      return n;
    });
  }, [scrollToSlide]);

  const prev = useCallback(() => {
    setSlide((s) => {
      const n = Math.max(s - 1, 0);
      scrollToSlide(n);
      return n;
    });
  }, [scrollToSlide]);

  // Keyboard navigation. Right / Down / PageDown / Space → next.
  // Left / Up / PageUp / Shift+Space → prev. Home / End for first/last.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowRight', 'ArrowDown', 'PageDown'].includes(e.key) ||
          (e.key === ' ' && !e.shiftKey)) {
        e.preventDefault();
        next();
      } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key) ||
                 (e.key === ' ' && e.shiftKey)) {
        e.preventDefault();
        prev();
      } else if (e.key === 'Home') {
        e.preventDefault();
        setSlide(0);
        scrollToSlide(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setSlide(TOTAL_SLIDES - 1);
        scrollToSlide(TOTAL_SLIDES - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, scrollToSlide]);

  // Track which slide is currently visible so the counter updates as the
  // user scrolls. Uses IntersectionObserver on each slide.
  useEffect(() => {
    const slides = document.querySelectorAll<HTMLElement>('.slide');
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.5) {
            const idx = Number(e.target.getAttribute('data-slide'));
            if (!Number.isNaN(idx)) setSlide(idx);
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <div className="deck surface" data-theme="light">
      {/* Slide 0 · Title */}
      <Slide n={0}>
        <div className="slide-eyebrow">Investor brief &middot; 2026</div>
        <h1 className="slide-title">
          A research-and-reasoning agent
          <br />
          that <em>gets you to STEP, fast.</em>
        </h1>
        <p className="slide-sub">
          Tell LMcad what part you need. It hands back a parametric model
          and a clean STEP file your team opens in CATIA, Creo, or
          SolidWorks. The boring start, taken care of.
        </p>
        <div className="slide-meta">
          <Brand />
        </div>
      </Slide>

      {/* Slide 1 · The problem */}
      <Slide n={1}>
        <div className="slide-eyebrow">The problem</div>
        <h2>
          First drafts are <em>slow.</em>
        </h2>
        <p className="slide-body">
          Every mechanical-design project starts with the same loss. Hours
          in CATIA or Creo just to sketch a bracket, a fastener, a housing
          concept. The tools are powerful, but their warm-up is long and
          their floor is high.
        </p>
        <p className="slide-body">
          Senior engineers spend their best hours on the first draft
          instead of the actual design. Juniors stall before the first
          extrude. The rest of the team waits on a STEP file before
          anyone can make a decision. India alone runs more than 1,700
          Global Capability Centres employing close to 1.9 million
          engineers. Every minute saved at the start compounds across
          millions of billable hours.
        </p>
        <p className="slide-cite">
          Sources: Zinnov-NASSCOM India GCC Landscape 2024 &middot;
          NASSCOM ER&amp;D Shift 2025.
        </p>
      </Slide>

      {/* Slide 2 · Today's tools don't close it */}
      <Slide n={2}>
        <div className="slide-eyebrow">Today&rsquo;s tools</div>
        <h2>
          The gap stays open <em>by design.</em>
        </h2>
        <ul className="slide-list">
          <li>
            <strong>Classical CAD</strong> like CATIA, Creo, NX, and
            SolidWorks. Precise, slow, and built for the engineer who
            already knows the menus.
          </li>
          <li>
            <strong>Text-to-mesh tools</strong> like Meshy, Sloyd, Luma,
            and Spline. Fast, but the output is throwaway triangle soup.
            Useless for any manufacturing pipeline.
          </li>
          <li>
            <strong>CAD copilots</strong> like SolidWorks Aura, Onshape AI
            Advisor, Creo+ Assistant, and Leo AI. They help inside an
            existing tool with macros, sketches, and constraint-solving.
            None of them fix the first-draft problem.
          </li>
          <li>
            <strong>Generative engines</strong> like nTop and Autodesk
            Project Bernini. Topology optimisation, lattices, and field
            work. Powerful for optimisation, not for ideation.
          </li>
        </ul>
        <p className="slide-body">
          Nothing on the market today combines chat input, parametric
          output as STEP and STL, and an agent that asks, researches,
          and remembers.*
        </p>
        <p className="slide-body small">
          *Zoo and Adam CAD ship chat-to-parametric output, but as
          one-shot generators. None of them ask, none of them research,
          none of them remember. That last part is the difference that
          matters.
        </p>
      </Slide>

      {/* Slide 3 · The solution */}
      <Slide n={3}>
        <div className="slide-eyebrow">The solution</div>
        <h2>
          Describe the part. <em>We draft it.</em>
        </h2>
        <p className="slide-body">
          LMcad emits a parametric model with named dimensions, a 3D
          preview, and a clean STEP. You iterate by chat or by slider.
          Hand-off is one download.
        </p>
        <p className="slide-body">
          Your CAD pipeline doesn&rsquo;t change. We slot in before the
          first serious sketch. Same toolchain, new entry point.
        </p>
      </Slide>

      {/* Slide 4 · How it works */}
      <Slide n={4}>
        <div className="slide-eyebrow">How it works</div>
        <h2>
          Three steps. <em>One draft.</em>
        </h2>
        <ol className="slide-list big">
          <li>
            <span className="num">01</span>
            <div>
              <strong>Describe.</strong> One sentence will do. Bolt
              patterns, fits, shaft sizes, mounting flanges.
            </div>
          </li>
          <li>
            <span className="num">02</span>
            <div>
              <strong>Generate.</strong> The drafter asks if the spec is
              incomplete, looks up what it doesn&rsquo;t know, and produces
              a parametric part with a clean STEP.
            </div>
          </li>
          <li>
            <span className="num">03</span>
            <div>
              <strong>Tweak or hand off.</strong> Drag a slider, re-prompt,
              or download the STEP and continue in your CAD.
            </div>
          </li>
        </ol>
      </Slide>

      {/* Slide 5 · More than drafting (the agent capability) */}
      <Slide n={5}>
        <div className="slide-eyebrow">More than drafting</div>
        <h2>
          Asks. Researches. <em>Remembers.</em>
        </h2>
        <ul className="slide-list">
          <li>
            <strong>Asks before drafting.</strong> If the spec
            doesn&rsquo;t pin down a tooth profile, an airfoil family, or
            a sealing method, LMcad asks before it draws. Better to pause
            for thirty seconds than produce something you&rsquo;d throw
            away.
          </li>
          <li>
            <strong>Researches what it doesn&rsquo;t know.</strong> When
            it&rsquo;s drafting an M0.5 module gear and isn&rsquo;t
            certain about the addendum, it goes and looks it up. Same with
            ISO thread pitches, weld throat sizes, fastener torques. Real
            numbers from real standards.
          </li>
          <li>
            <strong>Remembers what it learns.</strong> The first time
            LMcad makes a planetary carrier, it&rsquo;s a research
            project. The second time, it isn&rsquo;t. Past parts become
            reusable references, especially when assemblies start showing
            up.
          </li>
        </ul>
        <p className="slide-body small">
          As of 2026, no shipping CAD agent does all three. The closest
          commercial rivals (Zoo Zookeeper, Adam, Backflip) are still
          one-shot generators. ProCAD demonstrated the clarifying-question
          pattern academically (CVPR-track, Feb 2026) and Leo AI gets
          part-way to memory through PDM retrieval. The combination is
          unclaimed for now.
        </p>
        <p className="slide-cite">
          Sources: Zoo Zookeeper docs &middot; Onshape AI Advisor public
          docs &middot; Leo AI &ldquo;AI for Mech Engineers 2026&rdquo;
          &middot; ProCAD (arXiv 2602.03045).
        </p>
      </Slide>

      {/* Slide 6 · Impact */}
      <Slide n={6}>
        <div className="slide-eyebrow">Impact</div>
        <h2>
          What gets <em>saved.</em>
        </h2>
        <p className="slide-body">
          Three numbers we keep on the wall. Each is sourced, each is
          conservative, and each compounds for a Tier&nbsp;1 design office
          that ships thousands of parts a year.
        </p>
        <div className="slide-tsm">
          <div>
            <div className="label">Time</div>
            <div className="figure">30 min</div>
            <div className="note">
              LMcad ships a first-draft part in 10&ndash;15&nbsp;minutes;
              a fully iterated, parameter-tuned part lands in
              ~30&nbsp;minutes. The same first draft costs an engineer
              half a morning in CATIA. McKinsey models gen-AI
              accelerating R&amp;D processes 20&ndash;80% across
              industries; Volkswagen + Autodesk compressed a wheel
              concept from 1.5&nbsp;years to ~4&nbsp;months.
            </div>
          </div>
          <div>
            <div className="label">Money</div>
            <div className="figure">$360&ndash;560B / yr</div>
            <div className="note">
              Modelled annual value of gen-AI inside the R&amp;D function,
              10&ndash;15% of function cost (McKinsey). India already
              delivers 50&ndash;65% labour-cost savings vs.&nbsp;Europe on
              ER&amp;D (NASSCOM).
            </div>
          </div>
          <div>
            <div className="label">Efficiency</div>
            <div className="figure">25.1% faster</div>
            <div className="note">
              In an HBS &times; BCG randomised field experiment with 758
              consultants, AI users completed 12.2% more tasks, 25.1%
              faster, with 40% higher quality on work inside the AI
              frontier. Goldman Sachs models +1.5&nbsp;pp/yr labour
              productivity over a decade from generative AI.
            </div>
          </div>
        </div>
        <p className="slide-cite">
          Sources: LMcad internal benchmark, 2026 &middot; McKinsey
          &ldquo;Economic potential of generative AI&rdquo; Jun 2023
          &middot; Goldman Sachs Briggs/Hatzius Mar 2023 &middot;
          Dell&rsquo;Acqua et al. HBS Working Paper 24-013 (BCG field
          experiment) Sep 2023 &middot; Autodesk + Volkswagen customer
          story &middot; NASSCOM Global ER&amp;D Shift 2024.
        </p>
      </Slide>

      {/* Slide 7 · Markets overview */}
      <Slide n={7}>
        <div className="slide-eyebrow">Markets</div>
        <h2>
          Three segments. <em>One product.</em>
        </h2>
        <div className="slide-cols">
          <div>
            <h3>B2B</h3>
            <p>
              Tier&nbsp;1 and Tier&nbsp;2 mechanical design. Indian auto,
              aerospace, defence, and GCC R&amp;D. Roughly 25,000 to
              40,000 addressable firms globally.
            </p>
          </div>
          <div>
            <h3>B2C</h3>
            <p>
              The desktop 3D-printing market. India alone reached
              USD&nbsp;707&nbsp;M in 2024 with a USD&nbsp;5&nbsp;B
              trajectory by 2034. Most owners can print, very few can
              model.
            </p>
          </div>
          <div>
            <h3>Education</h3>
            <p>
              5,875 AICTE-approved colleges in India alone, with
              2.37&nbsp;lakh mechanical-engineering enrolments per year.
              AI-CAD in curriculum is greenfield.
            </p>
          </div>
        </div>
        <p className="slide-body" style={{ marginTop: 28, maxWidth: 880 }}>
          The chat UI lowers the barrier for B2C and education.
          Parametric STEP earns the right to enter B2B. Credit pricing
          scales across all three without per-seat friction.
        </p>
      </Slide>

      {/* Slide 8 · B2B detail (India-led) */}
      <Slide n={8}>
        <div className="slide-eyebrow">Market &middot; B2B</div>
        <h2>
          Tier&nbsp;1 &amp; Tier&nbsp;2 <em>mechanical design.</em>
        </h2>
        <p className="slide-body">
          Bharat Forge, Sundaram Fasteners, Sona Comstar, Motherson,
          Endurance, and Bosch India anchor an auto-component sector that
          hit USD&nbsp;80.2&nbsp;billion in FY25 and is on track for
          USD&nbsp;200&nbsp;B by 2030. India also runs roughly 1,700 GCCs
          with 1.9 million engineers and USD&nbsp;36.4&nbsp;B in ER&amp;D
          revenue, the largest captive engineering services base in the
          world. Globally, mechanical CAD is a USD&nbsp;6&nbsp;B per year
          market.
        </p>
        <p className="slide-body">
          LMcad is the draft accelerator that feeds the existing pipeline.
          We sell on rapid prototyping and reduced first-draft cost, not
          on replacing CATIA.
        </p>
        <div className="slide-tsm">
          <div>
            <div className="label">TAM</div>
            <div className="figure">$6.0B</div>
            <div className="note">Global mechanical CAD market, 2024</div>
          </div>
          <div>
            <div className="label">SAM</div>
            <div className="figure">$1.5–2.0B</div>
            <div className="note">Concept-stage workflow at firms with 10+ engineers, IN/NA/EU/JP/CN</div>
          </div>
          <div>
            <div className="label">SOM (3–5 yrs)</div>
            <div className="figure">$30–80M ARR</div>
            <div className="note">~500–1,500 paying firms, India-led, at ~$50k/yr blended</div>
          </div>
        </div>
        <p className="slide-cite">
          Sources: ACMA FY25 Press Release &middot; Zinnov-NASSCOM GCC
          Landscape 2024 &middot; Future Market Report (Mech CAD).
        </p>
      </Slide>

      {/* Slide 9 · B2C detail (India-led) */}
      <Slide n={9}>
        <div className="slide-eyebrow">Market &middot; B2C</div>
        <h2>
          The <em>3D printing</em> market.
        </h2>
        <p className="slide-body">
          India&rsquo;s 3D-printing market reached USD&nbsp;707&nbsp;M in
          2024, growing about 21% a year, projected to hit
          USD&nbsp;5.2&nbsp;B by 2034. South India holds 34.8% of the
          market, anchored by Bengaluru&rsquo;s aerospace cluster,
          Hyderabad&rsquo;s NCAM, and the Chennai EV corridor. WOL3D,
          3Ding, Bambu Lab India, Imaginarium, and think3D have made
          desktop printers mainstream. Globally, Bambu, Prusa, and
          Creality shipped roughly 13&nbsp;million units in 2024.
        </p>
        <p className="slide-body">
          The bottleneck has shifted from hardware to design. Most owners
          can print, very few can model. LMcad is the modeling layer.
        </p>
        <div className="slide-tsm">
          <div>
            <div className="label">TAM</div>
            <div className="figure">$5.9B</div>
            <div className="note">Global desktop 3D printing, 2024</div>
          </div>
          <div>
            <div className="label">SAM</div>
            <div className="figure">$0.8–1.2B</div>
            <div className="note">Design-tool wallet of active printer owners (~15M)</div>
          </div>
          <div>
            <div className="label">SOM (3–5 yrs)</div>
            <div className="figure">$10–30M ARR</div>
            <div className="note">200k–500k consumer subscribers at ~$50–60/yr</div>
          </div>
        </div>
        <p className="slide-cite">
          Sources: IMARC India 3D Printing Market 2024 &middot; Wohlers
          Report 2024/25 &middot; Manufactur3D India highlights 2024
          &middot; CONTEXT shipment data 2024.
        </p>
      </Slide>

      {/* Slide 10 · Education detail (India-led) */}
      <Slide n={10}>
        <div className="slide-eyebrow">Market &middot; Education</div>
        <h2>
          Teach <em>generative modeling.</em>
        </h2>
        <p className="slide-body">
          India runs 5,875 AICTE-approved technical institutions in
          2025-26, with a B.Tech intake of 14.90&nbsp;lakh seats and
          12.53&nbsp;lakh filled. Mechanical engineering alone counts
          2,36,909 enrolments this year. The 23 IITs, 31 NITs, and 25
          IIITs together produce only about 5% of India&rsquo;s engineers.
          The rest come from private and state colleges. India produces
          roughly 1.5 million engineering graduates every year.
        </p>
        <p className="slide-body">
          NEP&nbsp;2020 invites new electives but no AICTE circular has
          mandated AI-CAD in mechanical curricula yet. No incumbent
          AI-CAD vendor is running an India campus motion. The lane is
          wide open.
        </p>
        <div className="slide-tsm">
          <div>
            <div className="label">TAM</div>
            <div className="figure">$300–500M</div>
            <div className="note">~6M active engineering students globally</div>
          </div>
          <div>
            <div className="label">SAM</div>
            <div className="figure">$60–120M</div>
            <div className="note">Institutions that pay for CAD seats today</div>
          </div>
          <div>
            <div className="label">SOM (3–5 yrs)</div>
            <div className="figure">$3–8M ARR</div>
            <div className="note">100–300 site-license partnerships, India-anchored</div>
          </div>
        </div>
        <p className="slide-cite">
          Sources: AICTE 2025-26 dataset (via Education Today &amp; Sakshi
          Education) &middot; AICTE Model Syllabus portal &middot; ABET
          accreditation footprint Oct 2024.
        </p>
      </Slide>

      {/* Slide 11 · Why India */}
      <Slide n={11}>
        <div className="slide-eyebrow">Why India</div>
        <h2>
          Where the <em>work is.</em>
        </h2>
        <div className="slide-cols">
          <div>
            <h3>Manufacturing</h3>
            <p className="big">$80B</p>
            <p>
              Auto-component sector FY25, growing 14% a year, projected
              to hit $200&nbsp;B by 2030. ₹25,938&nbsp;Cr PLI-Auto
              envelope (FY23-FY27). Make-in-India and the National
              Strategy on Additive Manufacturing add policy-funded
              tailwinds.
            </p>
          </div>
          <div>
            <h3>Engineering services</h3>
            <p className="big">1,700+ GCCs</p>
            <p>
              Roughly 1.9&nbsp;million engineers across captive R&amp;D
              centres. India&rsquo;s share of global ER&amp;D outsourcing
              is forecast to rise from 17% in FY24 to 22% by 2030. Every
              productivity point compounds.
            </p>
          </div>
          <div>
            <h3>Education</h3>
            <p className="big">5,875</p>
            <p>
              AICTE-approved colleges, 14.90&nbsp;lakh B.Tech intake,
              2.37&nbsp;lakh mech-eng enrolments per year. Roughly
              1.5&nbsp;million engineering graduates a year. NEP-2020
              curriculum still being written.
            </p>
          </div>
        </div>
        <p className="slide-body" style={{ marginTop: 28 }}>
          The opening: land where Indian buyers feel the pain (concept-to-STEP
          throughput in Tier-1 GCCs), expand into AM service bureaus, and
          seed campuses now, before the 1.5 million-graduate cohort
          defaults to incumbent CAD.
        </p>
        <p className="slide-cite">
          Sources: ACMA FY25 &middot; Ministry of Heavy Industries (PLI-Auto)
          &middot; Zinnov-NASSCOM 2024 &middot; AICTE 2025-26 &middot;
          MeitY National Strategy on Additive Manufacturing 2022.
        </p>
      </Slide>

      {/* Slide 12 · Competitive landscape */}
      <Slide n={12}>
        <div className="slide-eyebrow">Competitive landscape</div>
        <h2>
          Where we sit on <em>the map.</em>
        </h2>
        <ul className="slide-list">
          <li>
            <strong>Direct rivals</strong> in text-to-CAD parametric. Zoo
            (KittyCAD, Madrona-backed), Adam (YC W25, $4.1M seed Oct
            2025), Backflip ($30M+, Markforged founders). All chasing
            chat-to-parametric output.
          </li>
          <li>
            <strong>Adjacent</strong> in text-to-mesh. Meshy, Sloyd, Luma
            Genie, Spline AI, CSM. Mesh-only output, useless for
            manufacturing, different buyer entirely.
          </li>
          <li>
            <strong>Incumbent copilots.</strong> SolidWorks Aura (beta Jul
            2025), Onshape AI Advisor, Creo+ AI Assistant (Sep 2025), Leo
            AI ($9.7M, 50k engineers, SOC&nbsp;2), MecAgent. They help
            inside an existing CAD. None of them solve the first-draft
            problem.
          </li>
          <li>
            <strong>The threat.</strong> Autodesk Project Bernini, when
            shipped into Fusion, becomes native text-to-B-rep inside one
            major CAD. Mitigation: LMcad is multi-CAD neutral and serves
            CATIA, Creo, and SolidWorks shops Bernini won&rsquo;t reach.
          </li>
        </ul>
        <p className="slide-cite">
          Sources: TechCrunch (Adam, Oct 2025) &middot; PTC, Dassault,
          Autodesk press &middot; Onshape App Store &middot; CADCrafter
          (CVPR 2025).
        </p>
      </Slide>

      {/* Slide 13 · Why we win */}
      <Slide n={13}>
        <div className="slide-eyebrow">Why we win</div>
        <h2>
          Four things <em>nobody else has all of.</em>
        </h2>
        <ul className="slide-list">
          <li>
            <strong>Asks. Researches. Remembers.</strong> No shipping CAD
            agent today combines clarifying questions, design-time web
            research, and persistent design memory. We verified that
            against Zoo, Adam, Backflip, Leo, Aura, Onshape AI Advisor,
            Creo+, and Bernini in May 2026.
          </li>
          <li>
            <strong>Companion, not replacement.</strong> Zoo and Adam are
            both bidding to be the place you do work. We stay out of the
            modeling app and feed yours instead. Procurement says yes
            faster.
          </li>
          <li>
            <strong>Parametric model, B-rep solids, STEP export.</strong>{' '}
            Named dimensions you can tweak inside LMcad; the export is a
            real B-rep solid in STEP (AP242) that round-trips into any
            CAD. Mesh-only tools are disqualified at the manufacturing
            pipeline gate.
          </li>
          <li>
            <strong>Credit pricing, browser-native.</strong> No per-seat
            lock-in. The same backend serves a single B.Tech student and a
            Tier&nbsp;1 GCC running fifty thousand generations a month.
            No plugin install, no IT review.
          </li>
        </ul>
      </Slide>

      {/* Slide 14 · Business model */}
      <Slide n={14}>
        <div className="slide-eyebrow">Business model</div>
        <h2>
          Credits. <em>Pay for parts, not chairs.</em>
        </h2>
        <div className="slide-cols">
          <div>
            <h3>Sketch</h3>
            <p className="big">&#8377;2,000 / mo</p>
            <p>
              200 credits. Solo engineers, makers, individual learners.
            </p>
          </div>
          <div>
            <h3>Studio</h3>
            <p className="big">&#8377;6,000 / mo</p>
            <p>
              1,000 credits, priority queue. Small teams, prosumer
              designers.
            </p>
          </div>
          <div>
            <h3>Practice</h3>
            <p className="big">Custom</p>
            <p>
              Custom credit pools, SSO, SOC&nbsp;2, dedicated capacity.
              Tier&nbsp;1 and Tier&nbsp;2 firms, GCCs, universities.
            </p>
          </div>
        </div>
        <p className="slide-body small" style={{ marginTop: 20 }}>
          One credit per LLM call. First draft and re-prompts each cost
          a credit; slider tweaks are free, because moving a parameter
          is just a value change in the parametric model, no LLM
          involved. The same metered backend serves a B.Tech student
          running 10 prompts a month, a maker running 200, and a
          Bharat Forge design office running 50,000.
        </p>
      </Slide>

      {/* Slide 15 · What we ship today, what's next */}
      <Slide n={15}>
        <div className="slide-eyebrow">What we ship, what&rsquo;s next</div>
        <h2>
          Already shipping. <em>And what&rsquo;s coming.</em>
        </h2>

        <div
          className="slide-twocol"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 36,
            marginTop: 8,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--accent)',
                marginBottom: 14,
              }}
            >
              Today
            </div>
            <ul className="slide-list">
              <li>
                <strong>Single parts</strong> at production-grade
                throughput. Brackets, fasteners, housings, gears, fixtures.
              </li>
              <li>
                <strong>Small assemblies</strong> with solid accuracy.
                Brackets and fasteners, motors and housings, valves and
                manifolds.
              </li>
              <li>
                <strong>Empirically validated.</strong> Every part is
                checked against the spec it came from before it lands in
                your inbox.
              </li>
            </ul>
          </div>

          <div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--accent)',
                marginBottom: 14,
              }}
            >
              What&rsquo;s next
            </div>
            <ul className="slide-list">
              <li>
                <strong>Scientifically validated parts.</strong> FEM and
                CFD inside the drafting loop. Stress, fluid flow, and
                thermal checks before the STEP comes out, so the part
                isn&rsquo;t just inspected, it&rsquo;s verified.
              </li>
              <li>
                <strong>Enterprise connectors.*</strong> When a Tier&nbsp;1
                wants LMcad to drop STEP straight into their CATIA
                workflow with their naming conventions and check-in
                rules, we build the adapter. Per-customer first,
                productised over time.
              </li>
              <li>
                <strong>University rollout.</strong> Per-student credit
                pools, classroom dashboards, an AICTE-friendly curriculum
                unit. IITs and NITs first, then the wider AICTE network.
              </li>
              <li>
                <strong>Marketplace partnerships.</strong> Printables,
                Thingiverse, MakerWorld, and Cults move tens of millions
                of model downloads every month. We sit underneath them as
                the modeling layer their users can&rsquo;t write
                themselves. Distribution and revenue share.
              </li>
            </ul>
            <p className="slide-body small" style={{ marginTop: 20 }}>
              *Enterprise connectors are built on demand, not a direct
              future plan. The first Tier&nbsp;1 that asks gets the
              first adapter.
            </p>
          </div>
        </div>
      </Slide>

      {/* Slide 16 · The ask + contact */}
      <Slide n={16}>
        <div className="slide-eyebrow">The ask</div>
        <h2>
          Let&rsquo;s build the <em>front of the bench.</em>
        </h2>
        <p className="slide-body">
          We&rsquo;re raising to extend runway through enterprise launch
          and our first ten Tier&nbsp;1 and Tier&nbsp;2 design wins,
          India-led. Use of funds: closing the gap to scientifically
          validated parts (FEM and CFD in the drafting loop), enterprise
          connectors for Tier-1 CATIA and Creo workflows, the campus
          motion across IITs and NITs, and the sales work that turns one
          engineer&rsquo;s love into a GCC site licence.
        </p>
        <div className="slide-contact">
          <div>
            <div className="op-label">Talk to us</div>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <br />
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer">
                Book a demo
              </a>
            </p>
          </div>
          <div>
            <div className="op-label">Brand</div>
            <p style={{ marginTop: 4 }}>
              <Brand size={20} />
            </p>
          </div>
        </div>
      </Slide>

      {/* Brand mark, slide counter · fixed UI overlay. */}
      <div className="deck-chrome">
        <div className="deck-chrome-brand">
          <Brand />
        </div>
        <div className="deck-chrome-counter">
          <span className="num">{String(slide + 1).padStart(2, '0')}</span>
          <span className="sep">/</span>
          <span className="total">{String(TOTAL_SLIDES).padStart(2, '0')}</span>
        </div>
      </div>

      <div
        className={`deck-nav-group${navVisible ? ' visible' : ''}`}
        onMouseEnter={() => {
          if (navTimerRef.current !== null) {
            window.clearTimeout(navTimerRef.current);
            navTimerRef.current = null;
          }
          setNavVisible(true);
        }}
        onMouseLeave={wakeNav}
      >
        <button
          type="button"
          className="deck-nav prev"
          onClick={prev}
          disabled={slide === 0}
          aria-label="Previous slide"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 12 L10 6 L16 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className="deck-nav next"
          onClick={next}
          disabled={slide === TOTAL_SLIDES - 1}
          aria-label="Next slide"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 8 L10 14 L16 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Slide({
  n,
  children,
}: {
  n: number;
  children: ReactNode;
}) {
  return (
    <section className="slide" data-slide={n}>
      <div className="slide-inner">{children}</div>
    </section>
  );
}
